import { useState } from 'react';

import { cx } from '@emotion/css';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, theme } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { Condition, useIsomorphicLayoutEffect } from '@revenuemarket/react';
import { Toast, Typography } from '@revenuemarket/ui';
import { assert, omit } from '@revenuemarket/utils';
import { Button, Form, Input, Radio, Tag } from 'antd';
import { Rule } from 'antd/es/form';
import * as yup from 'yup';

import CertificateFileUploadDrawer from '~components/Certificate/CertificateFileUploadDrawer';
import useCodef from '~hooks/useCodef';
import { css } from '~libs/emotion';
import { ConnectedCodefCertificate } from '~models/certificate';
import { CodefCertificateBase, RefinedCodefCert } from '~types/codef';
import { checkDuplicateCert } from '~utils/certificate';
import getRefinedCertData from '~utils/codef/getRefinedCertData';

interface CertificateLoginForm {
  password: string;
}

const LoginScheme = yup.object({
  password: yup.string().required('비밀번호를 입력해주세요'),
});

const getSchemeRule = (field: keyof yup.InferType<typeof LoginScheme>): Rule => ({
  validator: async (_, value) => {
    await LoginScheme.validateAt(field, { [field]: value });
  },
});

interface Props {
  drivePath: string;
  onConnect: (certificate: ConnectedCodefCertificate) => void | Promise<void>;
  registeredCertificates?: CodefCertificateBase[];
}

const CertificateConnector = ({ drivePath, onConnect, registeredCertificates = [] }: Props) => {
  const { requestExportCertificate, requestGetCertification } = useCodef();
  const [form] = Form.useForm<CertificateLoginForm>();
  const [certificates, setCertificates] = useState<RefinedCodefCert[]>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<RefinedCodefCert>();
  const [isLoading, setIsLoading] = useState(false);
  const areAllCertificatesRegistered =
    certificates.length > 0 &&
    registeredCertificates.length > 0 &&
    certificates.every((cert) =>
      registeredCertificates.some((registeredCert) => checkDuplicateCert(registeredCert, cert)),
    );

  const onFinish = async (values: CertificateLoginForm) => {
    try {
      assert(selectedCertificate != null);
      setIsLoading(true);

      const certificate = await requestExportCertificate({
        certPassword: values.password,
        certPath: selectedCertificate.raw['cert.der.path'],
        keyPath: selectedCertificate.raw['cert.key.path'],
      });
      await onConnect({
        ...omit(selectedCertificate, ['raw', 'status']),
        cert: certificate.CONVERT,
        password: values.password,
      });
    } catch {
      Toast.error('공동인증서 비밀번호가 올바르지 않습니다.');
    } finally {
      setSelectedCertificate(undefined);
      setIsLoading(false);
    }
  };

  useIsomorphicLayoutEffect(() => {
    requestGetCertification(drivePath).then((result) => {
      const availableCertificates = getRefinedCertData(result).filter(
        (cert) => cert?.status === 'NORMAL',
      );
      setCertificates(availableCertificates);
      setSelectedCertificate(
        availableCertificates.filter((certificate) =>
          registeredCertificates.every(
            (registeredCertificate) =>
              checkDuplicateCert(registeredCertificate, certificate) === false,
          ),
        )[0],
      );
    });

    return () => {
      setCertificates([]);
    };
  }, [drivePath, requestGetCertification]);

  return (
    <Flex direction="column" gap={16}>
      <ul>
        {certificates.map((certificate) => {
          const certRegistered = registeredCertificates.some((cert) =>
            checkDuplicateCert(cert, certificate),
          );
          return (
            <Flex
              align="center"
              key={certificate.certificateOwner}
              onClick={() => {
                if (certRegistered) return;
                setSelectedCertificate(certificate);
              }}
              className={cx(
                css`
                  cursor: pointer;
                  border-bottom: 1px solid ${theme.colorTokens.borderDefault};
                `,
                selectedCertificate === certificate &&
                  css`
                    background-color: ${theme.colorTokens.bgPrimary};
                  `,
              )}
            >
              <Flex
                align="center"
                className={css`
                  padding: 8px;
                `}
              >
                <Radio
                  disabled={certRegistered}
                  checked={selectedCertificate === certificate}
                  onClick={() => setSelectedCertificate(certificate)}
                />
              </Flex>
              <Flex
                direction="column"
                gap={8}
                className={css`
                  min-width: 0;
                  padding: 8px;
                `}
                align="center"
              >
                <Flex gap={8} align="center" justify="center">
                  {certRegistered && (
                    <Tag
                      style={{
                        width: 'fit-content',
                      }}
                    >
                      등록됨
                    </Tag>
                  )}
                  <Typography
                    variant="body1"
                    useEllipsis
                    color={
                      certRegistered ? colorTokens.textDisabled : theme.colorTokens.textDefault
                    }
                  >
                    {certificate.certificateOwner}
                  </Typography>
                </Flex>
                <Typography
                  variant="caption1"
                  color={
                    certRegistered ? colorTokens.textDisabled : theme.colorTokens.textDescription
                  }
                >
                  {certificate.certificateAuthority || '금융결제원'}
                  {' · '}
                  {certificate.certificateType || '전자세금용'}
                </Typography>
                <Typography
                  variant="caption1"
                  color={
                    certRegistered ? colorTokens.textDisabled : theme.colorTokens.textDescription
                  }
                >
                  만료일 {certificate.certificateExpireDate}
                </Typography>
              </Flex>
            </Flex>
          );
        })}
      </ul>
      {!areAllCertificatesRegistered && (
        <Condition
          expression={certificates.length > 0}
          then={() => (
            <Form form={form} onFinish={onFinish}>
              <Form.Item name="password" rules={[getSchemeRule('password')]}>
                <Input size="large" type="password" placeholder="공동인증서 비밀번호" />
              </Form.Item>
              <Form.Item<CertificateLoginForm> dependencies={['password']}>
                {({ getFieldsValue }) => {
                  const valid = (() => {
                    try {
                      LoginScheme.validateSync(getFieldsValue());
                      return true;
                    } catch {
                      return false;
                    }
                  })();

                  return (
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={isLoading}
                      disabled={selectedCertificate == null || !valid}
                      className={css`
                        width: 100%;
                      `}
                    >
                      공동인증서 등록
                    </Button>
                  );
                }}
              </Form.Item>
            </Form>
          )}
          else={() => (
            <Flex
              justify="center"
              align="center"
              className={css`
                padding: 48px;
              `}
            >
              <Typography variant="body1" color={theme.colorTokens.textDescription}>
                불러올 공동인증서가 없습니다.
              </Typography>
            </Flex>
          )}
        />
      )}
      <Flex gap={6} justify="flex-end" align="center">
        <Typography variant="caption2" color={theme.colorTokens.textDescription}>
          찾으시는 공동인증서가 안보이시나요?
        </Typography>
        <Button
          onClick={() => {
            overlays.open(({ isOpen, onClose }) => (
              <CertificateFileUploadDrawer
                isOpen={isOpen}
                onClose={onClose}
                onFinish={async (certificate) => {
                  await onConnect(certificate);
                  onClose();
                }}
              />
            ));
          }}
        >
          직접 찾기
        </Button>
      </Flex>
    </Flex>
  );
};

export default CertificateConnector;
